import React, { lazy, Suspense } from 'react';
import {
  Route,
  Routes,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import { PrivateRoute } from '../components/PrivateRoute/privateRoute';
import { AdminRoute } from '../components/AdminRoute/AdminRoute';
import TrainerGate from '../components/LiveStream/TrainerGate';
import { ToastContainer } from 'react-toastify';
import LoadingScreen from '../components/LoadingScreen';

// Lazy load route components - Default exports
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicyPage'));
const HelpLiveWorkouts = lazy(() => import('../pages/HelpLiveWorkouts'));
const Disclaimer = lazy(() => import('../pages/DisclaimerPage'));
const Terms = lazy(() => import('../pages/TermsPage'));
const MealShop = lazy(() => import('../pages/MealShop'));
const MealSuccess = lazy(() => import('../pages/MealShop/Success'));
const MealPlanPage = lazy(() => import('../pages/MealShop/Plan'));
const AdminMeals = lazy(() => import('../pages/AdminMeals'));
const AdminVideos = lazy(() => import('../pages/AdminVideos'));
const AdminOnsite = lazy(() => import('../pages/AdminOnsite'));
const AdminMusic = lazy(() => import('../pages/AdminMusic'));
const LiveStream = lazy(() => import('../components/LiveStream/index'));
const WatchStream = lazy(() => import('../components/LiveStream/watchStream'));
const CreateRecording = lazy(() => import('../pages/createRecording'));
const WorkoutPlayer = lazy(() =>
  import('../components/FreeWorkout/WorkoutPlayer')
);
const UserDashboard = lazy(() => import('../pages/userDashboard'));
const AdminLayout = lazy(() => import('../pages/AdminLayout'));
const AdminDashboard = lazy(() => import('../pages/AdminDashboard'));
const AdminCalendar = lazy(() => import('../pages/AdminCalendar'));
const AdminExercisesList = lazy(() => import('../pages/AdminExercises'));
const AdminNewsletter = lazy(() => import('../pages/AdminNewsletter'));
const AdminExerciseForm = lazy(() =>
  import('../pages/AdminExercises/ExerciseForm'),
);
const BookingInterface = lazy(() => import('../pages/BookingInterface'));
const WatchVideo = lazy(() => import('../components/LiveStream/watchVideo'));
const StripeSuccess = lazy(() => import('../pages/stripeSuccess'));
const StripeCanceled = lazy(() => import('../pages/stripeCanceled'));
const CoachingCheckout = lazy(() => import('../pages/CoachingCheckout'));
const ProgramCheckout = lazy(() => import('../pages/ProgramCheckout'));
const ProgramSuccess = lazy(() =>
  import('../pages/ProgramSuccess').then((m) => ({
    default: m.ProgramSuccess,
  })),
);
const CreateScheduledStream = lazy(
  () => import('../pages/createScheduledStream'),
);
const IntervalTimer = lazy(() => import('../components/IntervalTimer'));

// Lazy load route components - Named exports
const HomeGateway = lazy(() =>
  import('../pages/HomeGateway').then((m) => ({
    default: m.HomeGateway,
  })),
);
const HomeLandingOnsite = lazy(() =>
  import('../pages/HomeLandingOnsiteLight').then((m) => ({
    default: m.HomeLandingOnsiteLight,
  })),
);
const HomeLandingOnlineModern = lazy(() =>
  import('../pages/HomeLandingOnlineModern').then((m) => ({
    default: m.HomeLandingOnlineModern,
  })),
);
const HomeLandingStudio = lazy(() =>
  import('../pages/HomeLandingStudio').then((m) => ({
    default: m.HomeLandingStudio,
  })),
);
const HomeLanding8Week = lazy(() =>
  import('../pages/HomeLanding8Week').then((m) => ({
    default: m.HomeLanding8Week,
  })),
);
const HomeLanding12Week = lazy(() =>
  import('../pages/HomeLanding12Week').then((m) => ({
    default: m.HomeLanding12Week,
  })),
);
const HomePrograms = lazy(() =>
  import('../pages/HomePrograms').then((m) => ({
    default: m.HomePrograms,
  })),
);
const Register = lazy(() =>
  import('../pages/register').then((m) => ({ default: m.Register })),
);
const Login = lazy(() =>
  import('../pages/login').then((m) => ({ default: m.Login })),
);
const ResetPassword = lazy(() =>
  import('../pages/resetPassword').then((m) => ({ default: m.ResetPassword })),
);
const TrainingSuccess = lazy(() =>
  import('../pages/TrainingSuccess').then((m) => ({
    default: m.TrainingSuccess,
  })),
);
const MacronutrientCalculator = lazy(() =>
  import('../pages/macronutrientCalculator').then((m) => ({
    default: m.MacronutrientCalculator,
  })),
);
const MacroResults = lazy(() =>
  import('../components/MacroNutrientCalculatorComponent/results').then(
    (m) => ({
      default: m.MacroResults,
    }),
  ),
);
const CustomMealPlan = lazy(() =>
  import('../components/MacroNutrientCalculatorComponent/customMealPlan').then(
    (m) => ({ default: m.CustomMealPlan }),
  ),
);
const MealPlanLanding = lazy(() =>
  import('../pages/mealPlanLanding').then((m) => ({
    default: m.MealPlanLanding,
  })),
);
const ChallengeSignup = lazy(() =>
  import('../pages/challengeSignup').then((m) => ({
    default: m.ChallengeSignup,
  })),
);
const LinksPage = lazy(() =>
  import('../pages/links').then((m) => ({ default: m.LinksPage })),
);
const ResetStarter = lazy(() =>
  import('../pages/resetStarter').then((m) => ({ default: m.ResetStarter })),
);
const Freebies = lazy(() =>
  import('../pages/freebies').then((m) => ({ default: m.Freebies })),
);
const Apply = lazy(() =>
  import('../pages/apply').then((m) => ({ default: m.Apply })),
);
const Unauthorized = lazy(() =>
  import('../pages/unauthorized').then((m) => ({ default: m.Unauthorized })),
);
const Contact = lazy(() =>
  import('../pages/contact').then((m) => ({ default: m.Contact })),
);
const NextSteps = lazy(() =>
  import('../pages/nextSteps').then((m) => ({ default: m.NextSteps })),
);
const SubscriptionSuccess = lazy(() =>
  import('../pages/SubscriptionSuccess').then((m) => ({
    default: m.SubscriptionSuccess,
  })),
);
const VideoLibrary = lazy(() =>
  import('../pages/videoLibrary').then((m) => ({ default: m.VideoLibrary })),
);
const AccountSettings = lazy(() =>
  import('../pages/AccountSettings').then((m) => ({
    default: m.AccountSettings,
  })),
);

const RouteLoadingFallback = () => <LoadingScreen />;

// The members' video library. Someone signed out gets the same page in the
// public library instead of the login screen, so a link copied from the
// library keeps working: the video's card opens, and watching asks them to
// sign up.
const MembersVideos = () => {
  const { isAuthenticated, userLoading } = useSelector((state) => state.auth);
  const location = useLocation();
  if (!isAuthenticated && !userLoading) {
    return (
      <Navigate
        to={`${location.pathname.replace(/^\/dashboard/, '')}${location.search}`}
        replace
      />
    );
  }
  return (
    <PrivateRoute>
      <Outlet />
    </PrivateRoute>
  );
};

const IndexRoute = ({ sectionsRefs, handleScroll, auth }) => {
  return (
    <div className='main-content'>
      <ToastContainer theme='light' />
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          {/* Meal prep: private order page for clients (not in any menu). */}
          <Route path='/meals' element={<MealShop />} />
          <Route path='/meals/menu' element={<Navigate to='/meals' replace />} />
          <Route path='/meals/order/success' element={<MealSuccess />} />
          <Route path='/meals/plan/welcome' element={<MealSuccess />} />
          <Route path='/meals/plan/:token' element={<MealPlanPage />} />
          <Route
            path='/register'
            element={<Register exact isRegister={true} isModal={false} />}
          />
          <Route
            path='/login'
            element={<Login isLogin={true} isModal={false} />}
          />
          <Route
            path='/reset-password'
            element={<ResetPassword isResetPassword={true} isModal={false} />}
          />
          {/* The old meal admin pages now live at /admin/meals. */}
          <Route path='/meals/orders' element={<Navigate to='/admin/meals' replace />} />
          <Route path='/meals/orders-detail' element={<Navigate to='/admin/meals' replace />} />
          <Route path='/meals/manage-meals' element={<Navigate to='/admin/meals' replace />} />
          <Route
            path='/dashboard'
            element={
              <PrivateRoute>
                <UserDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path='/account'
            element={
              <PrivateRoute>
                <AccountSettings />
              </PrivateRoute>
            }
          />
          <Route
            path='/admin'
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          >
            <Route index element={<Navigate to='/admin/sessions' replace />} />
            <Route path='calendar' element={<AdminCalendar />} />
            <Route path='exercises' element={<AdminExercisesList />} />
            <Route path='exercises/new' element={<AdminExerciseForm />} />
            <Route path='exercises/:id' element={<AdminExerciseForm />} />
            <Route path='newsletter' element={<AdminNewsletter />} />
            <Route path='meals' element={<AdminMeals />} />
            <Route path='onsite' element={<AdminOnsite />} />
            <Route path='videos' element={<AdminVideos />} />
            <Route path='music' element={<AdminMusic />} />
            <Route path=':tab' element={<AdminDashboard />} />
          </Route>
          <Route
            path='/booking'
            element={
              <PrivateRoute>
                <BookingInterface />
              </PrivateRoute>
            }
          />
          <Route
            path='/live-stream'
            element={
              // Admins get the trainer page; anyone else goes to the live class.
              <TrainerGate>
                <LiveStream auth={auth} />
              </TrainerGate>
            }
          />
          <Route
            path='/create-recording'
            element={
              <PrivateRoute>
                <CreateRecording />
              </PrivateRoute>
            }
          />
          <Route
            path='/create-scheduled-stream'
            element={
              <PrivateRoute>
                <CreateScheduledStream />
              </PrivateRoute>
            }
          />
          <Route
            path='/create-interval-timer'
            element={
              <PrivateRoute>
                <IntervalTimer />
              </PrivateRoute>
            }
          />
          <Route path='/watch-stream/:id' element={<WatchStream />} />
          <Route path='/watch-video/:id' element={<WatchVideo />} />
          <Route path='/videos'>
            <Route index element={<Navigate to='all' replace />} />
            <Route path=':filter' element={<VideoLibrary />} />
            <Route path=':filter/:id' element={<VideoLibrary />} />
          </Route>
          <Route path='/dashboard/videos' element={<MembersVideos />}>
            <Route index element={<Navigate to='all' replace />} />
            <Route path=':filter' element={<VideoLibrary />} />
            <Route path=':filter/:id' element={<VideoLibrary />} />
          </Route>
          <Route path='/free-meal-plan' element={<MealPlanLanding />} />
          <Route path='/6-week-challenge' element={<ChallengeSignup />} />
          <Route path='/links' element={<LinksPage />} />
          <Route path='/reset-starter' element={<ResetStarter />} />
          <Route path='/free' element={<Freebies />} />
          <Route path='/apply' element={<Apply />} />
          <Route path='/nutrition'>
            <Route index element={<Navigate to='start' replace />} />
            <Route path='start' element={<MacronutrientCalculator />} />
            <Route path='macros' element={<MacroResults />} />
            <Route path='meal-plan' element={<CustomMealPlan />} />
          </Route>
          {/* The follow-along catalog now lives in the video library's
              Workouts tab; keep /workouts as a redirect and the chrome-free
              player at /workouts/:id. */}
          <Route
            path='/workouts'
            element={<Navigate to='/videos/workout' replace />}
          />
          <Route path='/workouts/:id' element={<WorkoutPlayer />} />
          <Route
            path='/dashboard/nutrition'
            element={
              <PrivateRoute>
                <Outlet />
              </PrivateRoute>
            }
          >
            <Route index element={<Navigate to='start' replace />} />
            <Route path='start' element={<MacronutrientCalculator />} />
            <Route path='macros' element={<MacroResults />} />
            <Route path='meal-plan' element={<CustomMealPlan />} />
          </Route>

          <Route
            path='/'
            element={
              auth.isAuthenticated ? (
                <Navigate to='/dashboard/videos/all' replace />
              ) : (
                <HomeGateway
                  sectionsRefs={sectionsRefs}
                  handleScroll={handleScroll}
                />
              )
            }
          />
          <Route path='/onsite' element={<HomeLandingOnsite />} />
          <Route path='/privacy' element={<PrivacyPolicy />} />
          <Route path='/disclaimer' element={<Disclaimer />} />
          <Route path='/terms' element={<Terms />} />
          <Route path='/help/live-workouts' element={<HelpLiveWorkouts />} />
          <Route
            path='/online'
            element={
              <HomeLandingOnlineModern
                sectionsRefs={sectionsRefs}
                handleScroll={handleScroll}
              />
            }
          />
          <Route
            path='/studio'
            element={<HomeLandingStudio sectionsRefs={sectionsRefs} />}
          />
          <Route
            path='/8-week-program'
            element={
              <HomeLanding8Week
                sectionsRefs={sectionsRefs}
                handleScroll={handleScroll}
              />
            }
          />
          <Route
            path='/12-week-program'
            element={
              <HomeLanding12Week
                sectionsRefs={sectionsRefs}
                handleScroll={handleScroll}
              />
            }
          />
          <Route path='/programs' element={<HomePrograms />} />
          <Route path='/training/success' element={<TrainingSuccess />} />

          <Route path='/contact' element={<Contact />} />
          <Route path='/next-steps' element={<NextSteps />} />
          <Route path='/confirmation/:id' element={<Navigate to='/meals' replace />} />
          <Route path='/checkout/success' element={<StripeSuccess />} />
          <Route path='/checkout/canceled' element={<StripeCanceled />} />
          <Route
            path='/subscription/success'
            element={<SubscriptionSuccess />}
          />
          <Route path='/coaching-checkout' element={<CoachingCheckout />} />
          <Route path='/program-checkout' element={<ProgramCheckout />} />
          <Route path='/program/success' element={<ProgramSuccess />} />
          <Route path='/unauthorized' element={<Unauthorized />} />
          <Route path='*' element={<p>There's nothing here: 404!</p>} />
        </Routes>
      </Suspense>
    </div>
  );
};

export default IndexRoute;
